import './Login.css'
import { FormEvent, ReactNode, useState } from 'react'
import { Form, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuth } from '../AuthContext';
import { changePassword, getErrorStatus, getValidationErrors } from '../api/Auth';
import { PageTitleBar } from './layout/PageBar';
import NewPasswordFields, { PasswordFormErrors, useNewPassword } from './password/NewPasswordFields';

/**
 * Смена пароля вошедшим пользователем. Сервер закрывает все прежние сессии
 * и сразу выдаёт новую cookie, так что после успеха пользователь остаётся в системе.
 */
function ChangePassword(): ReactNode {
    const { t } = useTranslation(['password']);
    const { user } = useAuth();

    // /api/auth/me отдаёт { name, roles }; name — логин, нужен для правила notUsername.
    const username = typeof user?.["name"] === 'string' ? user["name"] : undefined;

    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [isCurrentInvalid, setIsCurrentInvalid] = useState<boolean>(false);
    const newPassword = useNewPassword(username);

    const [errors, setErrors] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isDone, setIsDone] = useState<boolean>(false);

    const canSubmit = currentPassword.length > 0 && newPassword.isValid && !isSubmitting;

    function submitForm(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        if (!canSubmit) {
            return;
        }

        setErrors([]);
        setIsCurrentInvalid(false);
        setIsDone(false);
        setIsSubmitting(true);

        changePassword({ currentPassword: currentPassword, newPassword: newPassword.password }).then(() => {
            setIsDone(true);
            setCurrentPassword("");
            newPassword.reset();
        }).catch((error) => {
            if (getErrorStatus(error) === 401) {
                setIsCurrentInvalid(true);
                return;
            }
            const validationErrors = getValidationErrors(error);
            setErrors(validationErrors.length > 0 ? validationErrors : [t('change_error')]);
        }).finally(() => {
            setIsSubmitting(false);
        });
    }

    return (
        <>
            <PageTitleBar title={t('change_password')} />
            <div className='form-page'>
                <div className='form-page-container'>
                    <Form onSubmit={submitForm}>
                        <Form.Group className="login-form" controlId="formCurrentPassword">
                            <Form.Label>{t('current_password')}</Form.Label>
                            <Form.Control
                                type="password"
                                autoComplete="current-password"
                                placeholder={t('current_password_placeholder')}
                                value={currentPassword}
                                disabled={isSubmitting}
                                isInvalid={isCurrentInvalid}
                                onChange={e => {
                                    setCurrentPassword(e.currentTarget.value);
                                    setIsCurrentInvalid(false);
                                }}
                            />
                            <Form.Control.Feedback type="invalid">{t('current_password_incorrect')}</Form.Control.Feedback>
                        </Form.Group>

                        <NewPasswordFields state={newPassword} disabled={isSubmitting} />

                        <Button variant="primary" type="submit" disabled={!canSubmit}>
                            {t('change_submit')}
                        </Button>
                    </Form>
                    {isDone && <div className="password-form-success">{t('change_success')}</div>}
                    <PasswordFormErrors errors={errors} />
                </div>
            </div>
        </>
    )
}

export default ChangePassword;
